import { ChevronDown, ChevronUp, Store, TrendingDown, TrendingUp, Users, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import EmptyStateGuide from '../../components/analytics/EmptyStateGuide';
import MetricDefinitionsDrawer from '../../components/analytics/MetricDefinitionsDrawer';
import MetricTooltip from '../../components/analytics/MetricTooltip';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

// ─── Formatters ───────────────────────────────────────────────────────────────

function getDefaultFromDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
}

const formatCurrency = (value) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatNumber = (value) =>
    new Intl.NumberFormat('it-IT').format(value || 0);

const formatPct = (value) =>
    value === null || value === undefined ? '—' : `${value.toFixed(1)}%`;

const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    return new Date(isoStr).toLocaleDateString('it-IT');
};

// ─── Delta helpers ────────────────────────────────────────────────────────────

function computeDelta(current, prev) {
    if (prev === null || prev === undefined || prev === 0) return null;
    const abs = current - prev;
    const pct = parseFloat(((abs / prev) * 100).toFixed(1));
    return { abs, pct, up: abs >= 0 };
}

function DeltaBadge({ current, prev, invertColors }) {
    const d = computeDelta(current, prev);
    if (!d) return <span className="kpi-delta kpi-delta--neutral">n/a</span>;
    const sign = d.pct > 0 ? '+' : '';
    const isGood = invertColors ? !d.up : d.up;
    return (
        <span className={`kpi-delta kpi-delta--${isGood ? 'up' : 'down'}`}>
            {d.up ? '▲' : '▼'} {sign}{d.pct}%
        </span>
    );
}

// ─── Concentration helper ─────────────────────────────────────────────────────

function concentrationPct(venues, n, totalVolume) {
    if (!venues || venues.length === 0 || !totalVolume) return null;
    const top = venues.slice(0, n).reduce((sum, v) => sum + v.totalAmount, 0);
    return parseFloat(((top / totalVolume) * 100).toFixed(1));
}

// ─── Trend insights ───────────────────────────────────────────────────────────

function buildTrendInsights(trend) {
    if (!trend || trend.length === 0) return null;
    const total = trend.reduce((s, d) => s + d.amount, 0);
    const dailyAvg = total / trend.length;
    const best = trend.reduce((b, d) => (d.amount > b.amount ? d : b), trend[0]);
    return {
        dailyAvg: parseFloat(dailyAvg.toFixed(2)),
        bestDay: best.date,
        bestAmount: best.amount,
        bestCount: best.count,
    };
}

// ─── Auto summary ─────────────────────────────────────────────────────────────

function buildSummary(data) {
    if (!data) return null;
    const { summary, topVenues } = data;
    const vol = formatCurrency(summary.totalVolume);
    const tx = formatNumber(summary.totalTransactions);
    const active = summary.activeVenuesCount;
    const total = summary.totalVenuesCount;
    const c3 = concentrationPct(topVenues, 3, summary.totalVolume);
    let text = `Nel periodo selezionato Soontip ha generato ${vol} di mance su ${tx} transazioni, con ${active} local${active === 1 ? 'e attivo' : 'i attivi'} su ${total}.`;
    if (c3 !== null) text += ` I top 3 locali generano il ${c3}% del volume totale.`;
    if (summary.newActiveVenuesCount > 0)
        text += ` ${summary.newActiveVenuesCount} local${summary.newActiveVenuesCount === 1 ? 'e è nuovo' : 'i sono nuovi'} nel periodo.`;
    return text;
}

// ─── Main component ───────────────────────────────────────────────────────────

const DAILY_TABLE_LIMIT = 10;

export default function AnalyticsTraction() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0]
    });
    const [showInactiveVenues, setShowInactiveVenues] = useState(false);
    const [showAllDays, setShowAllDays] = useState(false);
    const [distributionVenueId, setDistributionVenueId] = useState(null);
    const [distributionData, setDistributionData] = useState(null);
    const [distributionLoading, setDistributionLoading] = useState(false);
    const [distributionError, setDistributionError] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await analyticsService.getTraction({ from: filters.from, to: filters.to });
            setData(result);
        } catch (err) {
            console.error('Failed to load traction data:', err);
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const loadDistribution = async (venueId) => {
        if (distributionVenueId === venueId) {
            setDistributionVenueId(null);
            setDistributionData(null);
            return;
        }
        try {
            setDistributionLoading(true);
            setDistributionError(null);
            setDistributionVenueId(venueId);
            const result = await analyticsService.getVenueWorkerDistribution(venueId, { from: filters.from, to: filters.to });
            setDistributionData(result);
        } catch (err) {
            console.error('Failed to load worker distribution:', err);
            setDistributionError(err.response?.data?.message || err.message || 'Errore nel caricamento della distribuzione');
        } finally {
            setDistributionLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        setShowInactiveVenues(false);
        setShowAllDays(false);
        setDistributionVenueId(null);
        setDistributionData(null);
    }, [loadData]);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>
            <PageGuide
                title="Crescita e utilizzo reale dei venue"
                summary="Volume mance, locali attivi vs inattivi e trend giornaliero. Indica quanta traction reale sta generando la rete di locali nel periodo."
            />

            {loading && !data && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento dati traction...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {data && (
                <>
                    {/* ── Toolbar ──────────────────────────────────────────── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            Periodo: {data.period?.from} → {data.period?.to}
                            {data.prevSummary && (
                                <span style={{ marginLeft: '8px', color: '#94a3b8' }}>
                                    {(() => {
                                        const cur = new Date(data.period.from);
                                        const dur = new Date(data.period.to) - new Date(data.period.from);
                                        const pFrom = new Date(cur - 86400000);
                                        const pFromStart = new Date(pFrom - dur);
                                        return `· vs ${pFromStart.toISOString().split('T')[0]} → ${pFrom.toISOString().split('T')[0]}`;
                                    })()}
                                </span>
                            )}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <MetricDefinitionsDrawer metricKeys={['totalTipVolume', 'successfulTransactions', 'activeVenues', 'venueActivationRate', 'averageTipAmount']} />
                            <button className="btn-refresh" onClick={loadData} disabled={loading} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                                {loading ? '⏳' : '🔄'} Aggiorna
                            </button>
                        </div>
                    </div>

                    {/* ── Sintesi automatica ───────────────────────────────── */}
                    {buildSummary(data) && (
                        <div className="overview-summary" style={{ marginBottom: '20px' }}>
                            <strong>Sintesi del periodo — </strong>{buildSummary(data)}
                        </div>
                    )}

                    {/* ── KPI principali ───────────────────────────────────── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Riepilogo Traction</h2>
                        <div className="kpi-grid">

                            <MetricTooltip metricKey="totalTipVolume">
                                <div className="kpi-card kpi-accent">
                                    <div className="kpi-icon"><TrendingUp size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Volume Mance</div>
                                        <div className="kpi-value">{formatCurrency(data.summary?.totalVolume)}</div>
                                        <div className="kpi-meta">
                                            <DeltaBadge current={data.summary?.totalVolume} prev={data.prevSummary?.totalVolume} />
                                            <span>vs periodo prec.</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="successfulTransactions">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><span style={{ fontSize: '24px', fontWeight: 700 }}>#</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Transazioni</div>
                                        <div className="kpi-value">{formatNumber(data.summary?.totalTransactions)}</div>
                                        <div className="kpi-meta">
                                            <DeltaBadge current={data.summary?.totalTransactions} prev={data.prevSummary?.totalTransactions} />
                                            <span>vs periodo prec.</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="activeVenues">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><Store size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Locali Attivi</div>
                                        <div className="kpi-value">
                                            {data.summary?.activeVenuesCount || 0}
                                            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#64748b', marginLeft: '4px' }}>
                                                / {data.summary?.totalVenuesCount || 0}
                                            </span>
                                        </div>
                                        <div className="kpi-meta">
                                            <span style={{ fontWeight: 600 }}>
                                                {data.summary?.totalVenuesCount > 0
                                                    ? formatPct((data.summary.activeVenuesCount / data.summary.totalVenuesCount) * 100)
                                                    : '—'}
                                            </span>
                                            <DeltaBadge current={data.summary?.activeVenuesCount} prev={data.prevSummary?.activeVenuesCount} />
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="venueActivationRate">
                                <div
                                    className="kpi-card kpi-warning"
                                    onClick={() => data.summary?.inactiveVenuesCount > 0 && setShowInactiveVenues(v => !v)}
                                    style={{ cursor: data.summary?.inactiveVenuesCount > 0 ? 'pointer' : 'default' }}
                                >
                                    <div className="kpi-icon"><TrendingDown size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Locali Inattivi</div>
                                        <div className="kpi-value">
                                            {data.summary?.inactiveVenuesCount || 0}
                                            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#64748b', marginLeft: '4px' }}>
                                                / {data.summary?.totalVenuesCount || 0}
                                            </span>
                                        </div>
                                        <div className="kpi-meta">
                                            <span style={{ fontWeight: 600 }}>
                                                {data.summary?.totalVenuesCount > 0
                                                    ? formatPct((data.summary.inactiveVenuesCount / data.summary.totalVenuesCount) * 100)
                                                    : '—'}
                                            </span>
                                            {data.summary?.inactiveVenuesCount > 0 && (
                                                <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#92400e' }}>
                                                    {showInactiveVenues ? '▲ Nascondi' : '▼ Vedi dettaglio'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="averageTipAmount">
                                <div className="kpi-card kpi-secondary">
                                    <div className="kpi-icon"><span style={{ fontSize: '24px' }}>Ø</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Mancia Media</div>
                                        <div className="kpi-value">
                                            {formatCurrency(
                                                data.summary?.totalTransactions > 0
                                                    ? data.summary.totalVolume / data.summary.totalTransactions
                                                    : 0
                                            )}
                                        </div>
                                        <div className="kpi-meta">
                                            <DeltaBadge
                                                current={data.summary?.totalTransactions > 0 ? data.summary.totalVolume / data.summary.totalTransactions : 0}
                                                prev={data.prevSummary?.totalTransactions > 0 ? data.prevSummary.totalVolume / data.prevSummary.totalTransactions : null}
                                            />
                                            <span>vs periodo prec.</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                        </div>
                    </div>

                    {/* ── Drill-down locali inattivi ───────────────────────── */}
                    {showInactiveVenues && data.inactiveVenues && data.inactiveVenues.length > 0 && (
                        <div className="trend-section" style={{ marginTop: 0, borderTop: '2px solid #fef3c7' }}>
                            <h2 className="section-title" style={{ color: '#92400e' }}>
                                Locali Inattivi nel Periodo ({data.inactiveVenues.length})
                            </h2>
                            <div className="trend-table-container">
                                <table className="trend-table">
                                    <thead>
                                        <tr>
                                            <th>Locale</th>
                                            <th>Tenant</th>
                                            <th>Ultima transazione</th>
                                            <th>Giorni inattivo</th>
                                            <th>Device attivo</th>
                                            <th>Azione suggerita</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.inactiveVenues.map(v => {
                                            const days = v.daysSinceLastTransaction;
                                            let action = '—';
                                            if (!v.hasActiveDevice) action = 'Verificare device';
                                            else if (!v.lastTransaction) action = 'Mai attivo — verificare onboarding';
                                            else if (days > 30) action = 'Contattare il locale';
                                            else action = 'Monitorare';
                                            return (
                                                <tr key={v.venueId}>
                                                    <td style={{ fontWeight: 500 }}>{v.venueName}</td>
                                                    <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{v.tenantName || '—'}</td>
                                                    <td>{v.lastTransaction ? formatDate(v.lastTransaction) : <span style={{ color: '#94a3b8' }}>Mai</span>}</td>
                                                    <td style={{ fontWeight: 500, color: days > 14 ? '#dc2626' : '#64748b' }}>
                                                        {days !== null ? `${days}gg` : '—'}
                                                    </td>
                                                    <td>
                                                        <span style={{ color: v.hasActiveDevice ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                                            {v.hasActiveDevice ? '✓ Sì' : '✗ No'}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '0.82rem', color: '#475569' }}>{action}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── KPI derivati ─────────────────────────────────────── */}
                    <div className="kpi-section">
                        <h2 className="section-title section-title--secondary">KPI Derivati</h2>
                        <div className="kpi-grid kpi-grid-sm">

                            <MetricTooltip metricKey="activeVenues">
                                <div className="kpi-card kpi-card-sm">
                                    <div className="kpi-content">
                                        <div className="kpi-label">Volume / Locale Attivo</div>
                                        <div className="kpi-value">
                                            {data.summary?.activeVenuesCount > 0
                                                ? formatCurrency(data.summary.totalVolume / data.summary.activeVenuesCount)
                                                : '—'}
                                        </div>
                                        <div className="kpi-meta"><span>Media per locale nel periodo</span></div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="successfulTransactions">
                                <div className="kpi-card kpi-card-sm">
                                    <div className="kpi-content">
                                        <div className="kpi-label">Transazioni / Locale Attivo</div>
                                        <div className="kpi-value">
                                            {data.summary?.activeVenuesCount > 0
                                                ? formatNumber(Math.round(data.summary.totalTransactions / data.summary.activeVenuesCount))
                                                : '—'}
                                        </div>
                                        <div className="kpi-meta"><span>Media per locale nel periodo</span></div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="activeVenues">
                                <div className="kpi-card kpi-card-sm">
                                    <div className="kpi-content">
                                        <div className="kpi-label">Nuovi Locali Attivi</div>
                                        <div className="kpi-value" style={{ color: '#6366f1' }}>
                                            {data.summary?.newActiveVenuesCount ?? '—'}
                                        </div>
                                        <div className="kpi-meta"><span>Assenti nel periodo prec.</span></div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="activeVenues">
                                <div className="kpi-card kpi-card-sm">
                                    <div className="kpi-content">
                                        <div className="kpi-label">Locali Ricorrenti</div>
                                        <div className="kpi-value" style={{ color: '#10b981' }}>
                                            {data.summary?.returningVenuesCount ?? '—'}
                                        </div>
                                        <div className="kpi-meta"><span>Attivi anche nel periodo prec.</span></div>
                                    </div>
                                </div>
                            </MetricTooltip>

                        </div>
                    </div>

                    {/* ── Concentrazione ───────────────────────────────────── */}
                    {data.topVenues && data.topVenues.length >= 3 && data.summary?.totalVolume > 0 && (
                        <div className="overview-summary" style={{ marginBottom: '20px' }}>
                            <strong>Concentrazione — </strong>
                            {(() => {
                                const c3 = concentrationPct(data.topVenues, 3, data.summary.totalVolume);
                                const c5 = concentrationPct(data.topVenues, 5, data.summary.totalVolume);
                                return (
                                    <>
                                        Top 3 locali = <strong>{c3}%</strong> del volume totale
                                        {data.topVenues.length >= 5 && <> · Top 5 locali = <strong>{c5}%</strong></>}
                                        {c3 > 80 && <span style={{ color: '#dc2626', marginLeft: '8px' }}>⚠ Traction molto concentrata</span>}
                                        {c3 <= 50 && <span style={{ color: '#16a34a', marginLeft: '8px' }}>✓ Traction ben distribuita</span>}
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* ── Top Locali ───────────────────────────────────────── */}
                    {data.topVenues && data.topVenues.length > 0 && (
                        <div className="trend-section">
                            <h2 className="section-title">Top Locali per Volume</h2>
                            <div className="trend-table-container">
                                <table className="trend-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Locale</th>
                                            <th>Tenant</th>
                                            <th>Volume</th>
                                            <th>% totale</th>
                                            <th>Δ vs prec.</th>
                                            <th>Transazioni</th>
                                            <th>Media</th>
                                            <th style={{ whiteSpace: 'nowrap' }}><Users size={13} style={{ verticalAlign: 'middle', marginRight: '3px' }} />Percettori</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.topVenues.map((venue, idx) => {
                                            const pct = data.summary?.totalVolume > 0
                                                ? ((venue.totalAmount / data.summary.totalVolume) * 100).toFixed(1)
                                                : null;
                                            const delta = computeDelta(venue.totalAmount, venue.prevTotalAmount);
                                            return (
                                                <tr key={venue.venueId}>
                                                    <td style={{ fontWeight: 600, color: '#6366f1' }}>
                                                        #{idx + 1}
                                                        {venue.isNew && (
                                                            <span style={{
                                                                marginLeft: '4px', fontSize: '0.65rem', fontWeight: 700,
                                                                background: '#6366f1', color: '#fff',
                                                                borderRadius: '4px', padding: '1px 4px'
                                                            }}>NEW</span>
                                                        )}
                                                    </td>
                                                    <td style={{ fontWeight: 500 }}>{venue.venueName}</td>
                                                    <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{venue.tenantName || '—'}</td>
                                                    <td style={{ fontWeight: 500 }}>{formatCurrency(venue.totalAmount)}</td>
                                                    <td style={{ color: '#64748b' }}>{pct !== null ? `${pct}%` : '—'}</td>
                                                    <td>
                                                        {delta ? (
                                                            <span className={`kpi-delta kpi-delta--${delta.up ? 'up' : 'down'}`} style={{ fontSize: '0.78rem' }}>
                                                                {delta.up ? '▲' : '▼'} {delta.pct > 0 ? '+' : ''}{delta.pct}%
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>n/a</span>
                                                        )}
                                                    </td>
                                                    <td>{formatNumber(venue.transactionCount)}</td>
                                                    <td style={{ color: '#64748b' }}>
                                                        {formatCurrency(
                                                            venue.transactionCount > 0 ? venue.totalAmount / venue.transactionCount : 0
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: venue.workerCount > 0 ? '#6366f1' : '#94a3b8', fontWeight: venue.workerCount > 0 ? 600 : 400 }}>
                                                        {venue.workerCount > 0 ? venue.workerCount : '—'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <button
                                                            className={`distribution-toggle-btn${distributionVenueId === venue.venueId ? ' distribution-toggle-btn--active' : ''}`}
                                                            onClick={() => loadDistribution(venue.venueId)}
                                                            title="Vedi distribuzione mance per percettore"
                                                        >
                                                            {distributionVenueId === venue.venueId
                                                                ? <><X size={12} style={{ verticalAlign: 'middle', marginRight: '2px' }} />Chiudi</>
                                                                : <><Users size={12} style={{ verticalAlign: 'middle', marginRight: '2px' }} />Distribuzione</>
                                                            }
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── Worker Distribution Drill-down ────────────────── */}
                    {distributionVenueId && (
                        <div className="distribution-panel">
                            <div className="distribution-panel-header">
                                <h3 className="distribution-panel-title">
                                    <Users size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                    Distribuzione mance per percettore
                                    {distributionData && (
                                        <span className="distribution-panel-venue">
                                            {distributionData.venueName}
                                            {distributionData.tenantName && (
                                                <span className="distribution-panel-tenant"> · {distributionData.tenantName}</span>
                                            )}
                                        </span>
                                    )}
                                </h3>
                                <button
                                    className="distribution-panel-close"
                                    onClick={() => { setDistributionVenueId(null); setDistributionData(null); }}
                                    title="Chiudi"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {distributionLoading && (
                                <div className="distribution-loading">
                                    <div className="spinner spinner--sm"></div>
                                    <span>Caricamento distribuzione...</span>
                                </div>
                            )}

                            {distributionError && (
                                <div className="alert alert-error" style={{ margin: '12px 0' }}>
                                    <strong>Errore:</strong> {distributionError}
                                </div>
                            )}

                            {distributionData && !distributionLoading && (
                                <>
                                    <div className="distribution-summary">
                                        <span><strong>Periodo:</strong> {distributionData.period?.from} → {distributionData.period?.to}</span>
                                        <span><strong>Transazioni riuscite:</strong> {formatNumber(distributionData.successfulTransactions)}</span>
                                        <span><strong>Volume locale:</strong> {formatCurrency(distributionData.totalVenueVolume)}</span>
                                        <span><strong>Totale distribuito:</strong> {formatCurrency(distributionData.totalDistributed)}</span>
                                        <span><strong>Percettori coinvolti:</strong> {distributionData.workerCount}</span>
                                        {distributionData.workerCount > 0 && (
                                            <span><strong>Media / percettore:</strong> {formatCurrency(distributionData.averagePerWorker)}</span>
                                        )}
                                    </div>

                                    {distributionData.warnings && distributionData.warnings.length > 0 && (
                                        <div className="distribution-warnings">
                                            {distributionData.warnings.map((w, i) => (
                                                <div key={i} className={`distribution-warning distribution-warning--${w.type}`}>
                                                    ⚠ {w.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {distributionData.workers && distributionData.workers.length > 0 ? (
                                        <>
                                            <div className="distribution-table-caveat">
                                                <strong>Importo distribuito:</strong> somma delle quote di mancia assegnate al percettore tramite TipTransfer nel periodo selezionato. Non indica necessariamente che il payout bancario sia già stato liquidato.
                                            </div>
                                            <div className="trend-table-container">
                                                <table className="trend-table distribution-table">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Percettore</th>
                                                            <th>Importo distribuito</th>
                                                            <th>% totale</th>
                                                            <th>Transazioni</th>
                                                            <th>Media / transazione</th>
                                                            <th>Stripe Connect</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {distributionData.workers.map((worker, idx) => (
                                                            <tr key={worker.destination} className={worker.stripeWarning ? 'distribution-row--warning' : ''}>
                                                                <td style={{ color: '#6366f1', fontWeight: 600 }}>#{idx + 1}</td>
                                                                <td style={{ fontWeight: 500 }}>{worker.workerName}</td>
                                                                <td style={{ fontWeight: 500 }}>{formatCurrency(worker.distributedAmount)}</td>
                                                                <td style={{ color: '#64748b' }}>{worker.pctOfTotal !== null ? `${worker.pctOfTotal}%` : '—'}</td>
                                                                <td>{formatNumber(worker.transactionCount)}</td>
                                                                <td style={{ color: '#64748b' }}>{formatCurrency(worker.averagePerTransaction)}</td>
                                                                <td>
                                                                    {worker.canReceivePayments
                                                                        ? <span className="distribution-stripe-ok">✓ Attivo</span>
                                                                        : <span className="distribution-stripe-warn">⚠ Non eleggibile</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        !distributionData.warnings?.some(w => w.type === 'no_distribution') && (
                                            <p style={{ color: '#64748b', fontSize: '0.85rem', padding: '12px 0' }}>
                                                Nessun dato di distribuzione disponibile per il periodo selezionato.
                                            </p>
                                        )
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── Trend giornaliero ────────────────────────────────── */}
                    {data.trend && data.trend.length > 0 && (() => {
                        const trendData = data.trend.map(item => ({
                            ...item,
                            label: new Date(item.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
                            media: item.count > 0 ? parseFloat((item.amount / item.count).toFixed(2)) : 0
                        }));
                        const insights = buildTrendInsights(data.trend);
                        const dailyAvgVal = insights?.dailyAvg || 0;
                        const tableRows = showAllDays ? [...trendData].reverse() : [...trendData].reverse().slice(0, DAILY_TABLE_LIMIT);

                        return (
                            <div className="trend-section">
                                <h2 className="section-title">Trend Giornaliero</h2>

                                {insights && (
                                    <div style={{
                                        display: 'flex', gap: '16px', flexWrap: 'wrap',
                                        marginBottom: '16px', padding: '10px 14px',
                                        background: '#f8fafc', borderRadius: '8px',
                                        border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#475569'
                                    }}>
                                        <span>
                                            <strong>Media giornaliera:</strong> {formatCurrency(insights.dailyAvg)}
                                        </span>
                                        <span style={{ color: '#cbd5e1' }}>·</span>
                                        <span>
                                            <strong>Picco del periodo:</strong> {formatDate(insights.bestDay)} — {formatCurrency(insights.bestAmount)} ({formatNumber(insights.bestCount)} transazioni)
                                        </span>
                                    </div>
                                )}

                                <div style={{ width: '100%', height: 260, marginBottom: '24px' }}>
                                    <ResponsiveContainer>
                                        <BarChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: '#64748b' }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e2e8f0' }}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: '#64748b' }}
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={v => `€${v}`}
                                                width={52}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    if (name === 'amount') return [formatCurrency(value), 'Volume'];
                                                    return [value, name];
                                                }}
                                                labelFormatter={label => `Data: ${label}`}
                                                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                            />
                                            <ReferenceLine
                                                y={dailyAvgVal}
                                                stroke="#6366f1"
                                                strokeDasharray="4 3"
                                                label={{ value: 'Media', position: 'insideTopRight', fontSize: 10, fill: '#6366f1' }}
                                            />
                                            <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="trend-table-container">
                                    <table className="trend-table">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Volume</th>
                                                <th>Transazioni</th>
                                                <th>Media</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableRows.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{formatDate(item.date)}</td>
                                                    <td style={{ fontWeight: 500 }}>{formatCurrency(item.amount)}</td>
                                                    <td>{formatNumber(item.count)}</td>
                                                    <td style={{ color: '#64748b' }}>{formatCurrency(item.media)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {trendData.length > DAILY_TABLE_LIMIT && (
                                    <button
                                        onClick={() => setShowAllDays(v => !v)}
                                        style={{
                                            marginTop: '8px', background: 'none', border: 'none',
                                            color: '#6366f1', cursor: 'pointer', fontSize: '0.85rem',
                                            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0'
                                        }}
                                    >
                                        {showAllDays
                                            ? <><ChevronUp size={14} /> Mostra meno</>
                                            : <><ChevronDown size={14} /> Vedi tutti i {trendData.length} giorni</>
                                        }
                                    </button>
                                )}
                            </div>
                        );
                    })()}

                    {data.trend && data.trend.length === 0 && (
                        <EmptyStateGuide
                            condition={true}
                            icon="📊"
                            title="Nessuna transazione nel periodo"
                            message="Non risultano mance nel periodo selezionato. Verifica che i locali abbiano device attivi e prova ad ampliare il range di date."
                        />
                    )}
                </>
            )}
        </AnalyticsLayout>
    );
}
